import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { SeoHead } from "@/components/SeoHead";

const FALLBACK = {
    name: "G21 Projekt GmbH",
    phone: "+49 6134 28 63 266",
    email: "info@g21-wiesbaden.de",
    address: "Gaugasse 19, 65203 Wiesbaden",
    directors: "Gabriele Scholze-Kurz, Alexander Kurz",
    registry: "Amtsgericht Wiesbaden, HRB 31370",
    vatid: "",
    project_address: "Gaugasse 21, 65203 Wiesbaden",
};

export default function Impressum() {
    const [c, setC] = useState(FALLBACK);
    useEffect(() => {
        api.get("/company").then((r) => setC({ ...FALLBACK, ...(r.data || {}) })).catch(() => setC(FALLBACK));
    }, []);

    return (
        <PageShell testId="impressum-page">
            <SeoHead
                seo={{
                    title: "Impressum · G21 Projekt GmbH · Neubau Wiesbaden-Biebrich",
                    description: "Impressum und Anbieterkennzeichnung der G21 Projekt GmbH – Bauträger des Neubauvorhabens Gaugasse 21 in Wiesbaden-Biebrich.",
                    canonical: "https://neubau-kaufen-wiesbaden.de/impressum",
                    ogImage: "https://neubau-kaufen-wiesbaden.de/og-cover.jpg",
                    lang: "de",
                }}
            />
            <div className="py-16">
            <div className="container-page max-w-3xl">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-ink mb-10">
                    <ArrowLeft size={16} /> Zurück
                </Link>

                <span className="overline">Rechtliches</span>
                <h1 className="font-heading text-4xl sm:text-5xl text-ink mt-4 mb-10">Impressum</h1>

                <div className="space-y-8 font-body text-stone-700">
                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Angaben gemäß § 5 TMG</h2>
                        <p className="leading-relaxed">
                            <strong>{c.name}</strong><br />
                            {c.address}<br />
                            Deutschland
                        </p>
                    </section>

                    {c.directors && (
                        <section>
                            <h2 className="font-heading text-xl text-ink mb-3">Vertretungsberechtigte Geschäftsführer</h2>
                            <p className="leading-relaxed">{c.directors}</p>
                        </section>
                    )}

                    {c.registry && (
                        <section>
                            <h2 className="font-heading text-xl text-ink mb-3">Registereintrag</h2>
                            <p className="leading-relaxed">{c.registry}</p>
                        </section>
                    )}

                    {c.vatid && (
                        <section>
                            <h2 className="font-heading text-xl text-ink mb-3">Umsatzsteuer-ID</h2>
                            <p className="leading-relaxed">{c.vatid}</p>
                        </section>
                    )}

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Kontakt</h2>
                        <p className="leading-relaxed">
                            Telefon: <a href={`tel:${(c.phone || "").replace(/\s/g, "")}`} className="text-brand-600 hover:underline">{c.phone}</a><br />
                            E-Mail: <a href={`mailto:${c.email}`} className="text-brand-600 hover:underline">{c.email}</a>
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Bauvorhaben</h2>
                        <p className="leading-relaxed">
                            Projektadresse: {c.project_address}
                        </p>
                    </section>

                    {c.architect && c.architect.name && (
                        <section>
                            <h2 className="font-heading text-xl text-ink mb-3">Architekt des Bauvorhabens</h2>
                            <p className="leading-relaxed">
                                <strong>{c.architect.name}</strong>
                                {c.architect.subtitle ? <> · {c.architect.subtitle}</> : null}<br />
                                {c.architect.owner}<br />
                                {c.architect.address}<br />
                                {c.architect.phone && (
                                    <>Telefon: <a href={`tel:${(c.architect.phone || "").replace(/\s/g, "")}`} className="text-brand-600 hover:underline">{c.architect.phone}</a><br /></>
                                )}
                                {c.architect.email && (
                                    <>E-Mail: <a href={`mailto:${c.architect.email}`} className="text-brand-600 hover:underline">{c.architect.email}</a><br /></>
                                )}
                                {c.architect.url && (
                                    <a href={c.architect.url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
                                        Profil bei der AK Hessen
                                    </a>
                                )}
                            </p>
                            {c.architect.note && (
                                <p className="text-xs text-stone-500 mt-3">{c.architect.note}</p>
                            )}
                        </section>
                    )}

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
                        <p className="leading-relaxed">
                            {c.directors || c.name}<br />
                            {c.address}
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Haftung für Inhalte</h2>
                        <p className="leading-relaxed text-sm">
                            Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf
                            diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis
                            10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet,
                            übermittelte oder gespeicherte fremde Informationen zu überwachen oder
                            nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">Bildnachweise</h2>
                        <p className="leading-relaxed text-sm">
                            Visualisierungen / 3D-Renderings: Bauherr/Architekt. Beispiel-Innenfotos
                            zeigen ggf. Sonderausstattung – Inventar nicht inbegriffen.
                            Lagebild „Biebricher Schloss": Pexels.
                        </p>
                    </section>

                    <p className="text-xs text-stone-500 pt-4 border-t border-stone-200">
                        Stand: {new Date().toLocaleDateString("de-DE")}.
                    </p>
                </div>
            </div>
            </div>
        </PageShell>
    );
}
